import { Button } from '@hologram-dimension/button';
import { TextInput } from '@hologram-dimension/text-input';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import styles from './SimNameEdit.module.css';

interface SimNameEditProps {
  existingName?: string;
  onSave: (name: string) => void;
  onCancel: () => void;
}

interface FormValues {
  newName?: string;
}

const SimNameEdit: FC<SimNameEditProps> = ({ existingName, onSave, onCancel }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [firstRender, setFirstRender] = useState(true);

  const { register, handleSubmit, setFocus, watch } = useForm<FormValues>({
    defaultValues: { newName: existingName },
  });

  const onSubmit = useCallback<SubmitHandler<FormValues>>(
    (data) => {
      setIsProcessing(true);
      const newName = data.newName?.trim();
      if (
        !newName ||
        newName.length === 0 ||
        !existingName ||
        newName.localeCompare(existingName) === 0
      ) {
        setFocus('newName', { shouldSelect: true });
        setIsProcessing(false);
        return;
      }

      onSave(newName);
    },
    [existingName, onSave, setFocus]
  );

  useEffect(() => {
    setFocus('newName', { shouldSelect: true });
    setFirstRender(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryCancel = useCallback(() => {
    if (!existingName) {
      onCancel();
      return;
    }

    if ((watch('newName')?.localeCompare(existingName) ?? 0) === 0) {
      onCancel();
    } else {
      setFocus('newName', { shouldSelect: true });
    }
  }, [existingName, onCancel, setFocus, watch]);

  const formKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        tryCancel();
      }
    },
    [tryCancel]
  );

  const formRef = useRef<HTMLFormElement>(null);

  const bodyClick = useCallback(
    (e: MouseEvent) => {
      if (firstRender) {
        return;
      }
      if (!formRef.current?.contains(e.target as Node)) {
        e.stopPropagation();
        e.preventDefault();
        tryCancel();
      }
    },
    [firstRender, tryCancel]
  );

  // TODO: This is a poor workaround to avoid using floating UI stuff for a non-floating component. Perhaps look at an alternative that is lighter? Or just use floating UI anyway?
  useEffect(() => {
    document.addEventListener('keyup', formKeyUp);
    document.addEventListener('click', bodyClick);

    return () => {
      document.removeEventListener('keyup', formKeyUp);
      document.removeEventListener('click', bodyClick);
    };
  }, [formKeyUp, bodyClick]);

  return (
    <form className={styles.wrapper} onSubmit={handleSubmit(onSubmit)} ref={formRef}>
      <TextInput
        id="sim-new-name"
        type="text"
        className={styles.textInput}
        inputProps={{ autoComplete: 'off', maxLength: 32 }}
        {...register('newName')}
      />
      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
        >
          Cancel
        </Button>
        <Button type="submit" loading={isProcessing}>
          Save
        </Button>
      </div>
    </form>
  );
};

export default SimNameEdit;
